import React, { useState, useRef } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import {
  optimizeImageUrl,
  optimizeVideoUrl
} from '../../utils/videoLinkOptimize';
import { useMediaQuery } from '../../hooks/mediaQuery';
import { useRouter } from 'next/router';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { type Swiper as SwiperType } from 'swiper';
import {
  LeftSwipperArrows,
  RightSwipperArrows
} from '../../shared-components/SwipperArrows/SwipperArrows';
SwiperCore.use([Navigation]);

const StyledVideo = styled.video``;

const NavigationButton = styled.div`
  background: #e2e2e2;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
`;

interface GalleryProps {
  media: {
    url: string;
    alt: string;
    name: string;
  }[];
  productName?: string;
  productSlug?: string;
  cardMessage?: string;
}
const StyledSwiper = styled(Swiper)`
  .swiper-pagination-bullet {
    background-color: white;
    opacity: 0.8;
  }
  .swiper-pagination-bullet-active {
    background-color: #1f2c5b;
    opacity: 1;
  }
`;
const PhotosHeader = ({
  media,
  productName,
  productSlug,
  cardMessage
}: GalleryProps) => {
  const mediaQuery = useMediaQuery(640);
  const router = useRouter();
  const handleShowAllClick = () => {
    router.push(`/tours/tour-gallery?slug=${productSlug}`);
  };
  const handleImageClick = (index: number) => {
    router.push(`/tours/tour-gallery?slug=${productSlug}&index=${index}`);
  };
  const [isHoveredShowAll, setIsHoveredShowAll] = useState(false);
  const [isHoveredPhotos, setIsHoveredPhotos] = useState(false);
  const handleMouseEnterShowAll = () => setIsHoveredShowAll(true);
  const handleMouseLeaveShowAll = () => setIsHoveredShowAll(false);

  const handleMouseEnterPhotos = () => setIsHoveredPhotos(true);
  const handleMouseLeavePhotos = () => setIsHoveredPhotos(false);

  const [isLastSlide, setIsLastSlide] = useState(false);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);

  const onBeforeInit = (Swiper: SwiperType): void => {
    if (typeof Swiper.params.navigation !== 'boolean') {
      const navigation = Swiper.params.navigation;
      if (navigation) {
        navigation.prevEl = prevRef.current;
        navigation.nextEl = nextRef.current;
      }
    }
  };

  const handleSlideChange = (swiper: SwiperType) => {
    setIsFirstSlide(swiper.isBeginning);
    setIsLastSlide(swiper.isEnd);
  };

  return (
    <>
      <div className="relative hidden mmd:flex w-full mx-auto">
        {cardMessage && cardMessage === 'AWARD WINNER!' && (
          <div className="absolute z-30 max-w-[300px] lg:max-w-[250px] py-2.5 px-2.5 md:top-2 md:left-2 xl:top-4 xl:left-4">
            <Image
              src="/award-winner.png"
              width={100}
              height={100}
              quality={100}
              alt="award-winner-logo"
              className="w-[70px] h-[76px] xl:w-[90px] xl:h-[106px]"
            />
          </div>
        )}

        <div
          onClick={handleShowAllClick}
          className="absolute bottom-[-22px] right-0 flex justify-center items-center z-10"
        >
          <button className="flex text-[#333] items-center gap-2 w-[296px] h-[50px] bg-white font-semibold rounded-full shadow-lg border border-gray-300 hover:shadow-xl transition">
            <div
              onMouseEnter={handleMouseEnterPhotos}
              onMouseLeave={handleMouseLeavePhotos}
              className="flex w-[50%] justify-center border-r border-r-[#A3ABBA]"
            >
              <Image
                src={
                  isHoveredPhotos
                    ? '/hovered-photo-gallery.svg'
                    : '/photo-gallery.svg'
                }
                width={24}
                height={24}
                alt="local"
                className="mr-2"
              />{' '}
              <span
                className={` ${
                  isHoveredPhotos
                    ? 'text-primary font-black underline'
                    : 'text-[#333]'
                }`}
              >{`${media?.length} photos`}</span>
            </div>
            <div
              onMouseEnter={handleMouseEnterShowAll}
              onMouseLeave={handleMouseLeaveShowAll}
              className="flex gap-2 w-[50%]"
            >
              {' '}
              <Image
                src={isHoveredShowAll ? '/hoveredShowAll.svg' : '/showAll.svg'}
                width={24}
                height={24}
                alt="show all icon"
              />{' '}
              <span
                className={` ${
                  isHoveredShowAll
                    ? 'text-primary font-black underline'
                    : 'text-[#333]'
                } `}
              >
                Show All
              </span>{' '}
            </div>
          </button>
        </div>
        <div className="h-[420px] relative w-full">
          <div className="grid grid-cols-2 gap-2">
            <div
              className="w-full h-[420px] cursor-pointer"
              onClick={() => handleImageClick(0)}
            >
              {media?.length > 0 &&
                media[0] &&
                (media[0].url.includes('jpg') ||
                media[0].url.includes('png') ? (
                  <Image
                    src={optimizeImageUrl(media[0].url)}
                    alt={media[0].alt || `${productName} image 1`}
                    quality={60}
                    fetchPriority="high"
                    width={600}
                    height={420}
                    loading="eager"
                    decoding="async"
                    className="h-[420px] object-cover w-full rounded-[8px]"
                    placeholder="blur"
                    blurDataURL="/placeholder.webp"
                    priority={true}
                  />
                ) : (
                  <StyledVideo
                    playsInline
                    autoPlay={!mediaQuery}
                    controls={mediaQuery}
                    muted
                    loop={!mediaQuery}
                    className="h-[420px] w-full object-cover rounded-[8px]"
                    src={optimizeVideoUrl(media[0]?.url)}
                    onClick={e => e.preventDefault()}
                  />
                ))}
            </div>

            <div className="w-full h-[420px] grid grid-cols-2 grid-rows-2 gap-2">
              {media?.slice(1, 5).map((mediaItem, index) => (
                <div
                  key={mediaItem.url}
                  onClick={() => handleImageClick(index + 1)}
                  className="cursor-pointer"
                >
                  {mediaItem.url.includes('jpg') ||
                  mediaItem.url.includes('png') ? (
                    <Image
                      src={optimizeImageUrl(mediaItem.url)}
                      alt={mediaItem.alt || `${productName} image ${index + 2}`}
                      quality={70}
                      width={300}
                      height={210}
                      className="h-[206px] object-cover w-full rounded-[8px]"
                      placeholder="blur"
                      blurDataURL="/placeholder.webp"
                      priority={index === 0}
                    />
                  ) : (
                    <StyledVideo
                      playsInline
                      autoPlay={!mediaQuery}
                      controls={mediaQuery}
                      muted
                      loop={!mediaQuery}
                      className="h-[206px] w-full object-cover rounded-[8px]"
                      src={optimizeVideoUrl(mediaItem.url)}
                      onClick={e => e.preventDefault()}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="relative mmd:hidden w-full">
        <div className="h-[300px] w-full">
          <div className="absolute top-1/2 z-20 -translate-y-1/2 flex w-full justify-between px-2">
            <NavigationButton ref={prevRef}>
              <LeftSwipperArrows isFirstSlide={isFirstSlide} />
            </NavigationButton>
            <NavigationButton ref={nextRef}>
              <RightSwipperArrows isLastSlide={isLastSlide} />
            </NavigationButton>
          </div>
          <StyledSwiper
            spaceBetween={10}
            slidesPerView="auto"
            pagination={{
              clickable: true
            }}
            onSlideChange={handleSlideChange}
            onBeforeInit={onBeforeInit}
            navigation={{
              prevEl: prevRef?.current,
              nextEl: nextRef?.current
            }}
            breakpoints={{
              320: { slidesPerView: 1 },
              480: { slidesPerView: 2 },
              640: { slidesPerView: 3 }
            }}
            className="h-full w-full"
          >
            {media?.map((mediaItem, index) => (
              <SwiperSlide key={mediaItem.url} className="h-full">
                {mediaItem.url.includes('jpg') ||
                mediaItem.url.includes('png') ? (
                  <Image
                    src={optimizeImageUrl(mediaItem.url)}
                    alt={mediaItem.alt || `${productName} image ${index + 1}`}
                    quality={70}
                    width={400}
                    height={300}
                    className="object-cover w-full h-full rounded-[8px]"
                    placeholder="blur"
                    blurDataURL="/placeholder.webp"
                    priority={index === 0}
                  />
                ) : (
                  <StyledVideo
                    playsInline
                    autoPlay={!mediaQuery}
                    controls={mediaQuery}
                    muted
                    loop={!mediaQuery}
                    className="object-cover w-full h-full rounded-[8px]"
                    src={optimizeVideoUrl(mediaItem.url)}
                    onClick={e => e.preventDefault()}
                  />
                )}
              </SwiperSlide>
            ))}
          </StyledSwiper>
        </div>
      </div>
    </>
  );
};

export default PhotosHeader;
